/**
 * UserMenu — Top-right user avatar button with popover containing identity,
 * theme selector (light / dark / system), settings link, and sign-out action.
 *
 * Provides a consistent portal-style top-right header across all domain shells.
 * Consumed by createShell() when a `user` prop is supplied; falls back to the
 * standalone ThemeToggle atom when no user is available.
 *
 * @layer widget
 * @package @plerity/dashboard-shared
 */

import { Lock, LogOut, Monitor, Moon, Settings, Sun } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useThemeStore } from "../../stores/themeStore";

// ─── Public types ─────────────────────────────────────────────────────────────

export interface UserInfo {
  /** Display name shown in the menu header (first + last, or alias) */
  name: string;
  /** Email address shown under the name */
  email: string;
  /** Optional avatar image URL — falls back to initials when absent */
  avatarUrl?: string;
}

export interface UserMenuProps {
  /** Authenticated user information */
  user: UserInfo;
  /**
   * Client-side route to the full settings page (e.g. '/settings').
   * When provided, a "Settings" link is shown in the menu.
   * Omit to hide the Settings link.
   */
  settingsRoute?: string;
  /**
   * Full URL to the security / password-change page (e.g.
   * 'https://app.animasmarketing.com/settings/security').
   * Uses an `<a href>` (not react-router Link) because this typically
   * navigates to the portal on a different domain.
   * Omit to hide the Security link.
   */
  securityRoute?: string;
  /**
   * Called when the "Sign out" item is activated.
   * Omit to hide the Sign out button (useful in unauthenticated previews).
   */
  onSignOut?: () => void;
}

// ─── Internal constants ───────────────────────────────────────────────────────

const THEMES = [
  { value: "light" as const, label: "Light", icon: Sun },
  { value: "dark" as const, label: "Dark", icon: Moon },
  { value: "system" as const, label: "System", icon: Monitor },
] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Return 1-2 uppercase initials from a display name. */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0][0] ?? "?").toUpperCase();
  return ((parts[0][0] ?? "") + (parts[parts.length - 1][0] ?? "")).toUpperCase();
}

// ─── Component ────────────────────────────────────────────────────────────────

export function UserMenu({ user, settingsRoute, securityRoute, onSignOut }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);

  // Close on click outside the popover
  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  const initials = getInitials(user.name);
  const hasActions = Boolean(settingsRoute) || Boolean(securityRoute) || Boolean(onSignOut);

  return (
    <div ref={menuRef} className="relative">
      {/* ── Avatar / trigger button ──────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`${user.name} — open account menu`}
        className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-brand text-sm font-semibold text-brand-fg transition-colors hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus focus-visible:ring-offset-2 dark:ring-offset-gray-900"
      >
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt={user.name} className="h-full w-full object-cover" />
        ) : (
          <span aria-hidden="true">{initials}</span>
        )}
      </button>

      {/* ── Dropdown panel ───────────────────────────────────────────────── */}
      {open && (
        <div
          role="menu"
          aria-label="Account menu"
          className="absolute right-0 top-11 z-50 w-64 rounded-xl border border-light bg-surface shadow-lg"
        >
          {/* Identity */}
          <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
            <p className="truncate text-sm font-semibold text-fg">{user.name}</p>
            <p className="truncate text-xs text-muted">{user.email}</p>
          </div>

          {/* Theme selector */}
          <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-700">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">Theme</p>
            {/* biome-ignore lint/a11y/useSemanticElements: role="group" on a <div> is the established fleet convention for a non-form button/toggle group (matches ui/UserMenu.astro's "Color theme" group and the medspa-charting treatment-area groups); <fieldset> is for form-control grouping and would be inconsistent here. */}
            <div className="flex gap-1" role="group" aria-label="Theme selection">
              {THEMES.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={theme === value}
                  onClick={() => setTheme(value)}
                  aria-label={label}
                  title={label}
                  className={`flex flex-1 flex-col items-center gap-1 rounded-lg border px-2 py-2 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus ${
                    theme === value
                      ? "border-info-500 bg-info-50 text-info-700 dark:border-info-400 dark:bg-info-900/20 dark:text-info-300"
                      : "border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500 dark:hover:bg-gray-700"
                  }`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Actions: Settings + Sign out */}
          {hasActions && (
            <div className="p-1">
              {settingsRoute && (
                <Link
                  to={settingsRoute}
                  role="menuitem"
                  onClick={() => setOpen(false)}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus dark:text-gray-300"
                >
                  <Settings className="h-4 w-4" aria-hidden="true" />
                  Settings
                </Link>
              )}
              {securityRoute && (
                <a
                  href={securityRoute}
                  role="menuitem"
                  onClick={() => setOpen(false)}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus dark:text-gray-300"
                >
                  <Lock className="h-4 w-4" aria-hidden="true" />
                  Security
                </a>
              )}
              {onSignOut && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    onSignOut();
                  }}
                  className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus dark:text-gray-300"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  Sign out
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
